import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Page, StatCard, Section, formatBRL, SeverityBadge } from "./_shared";
import { Heart, AlertTriangle, Package, DollarSign, ShoppingCart, ArrowRight } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { Link } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-72" />
      </div>
    );
  }

  const grade = data.health.grade;
  const gradeColor =
    grade === "A" ? "text-emerald-400" :
    grade === "B" ? "text-primary" :
    grade === "C" ? "text-yellow-400" :
    grade === "D" ? "text-orange-400" :
    grade === "F" ? "text-red-500" :
    "text-muted-foreground";
  const gradeLabel =
    grade === "A" ? "Excelente" :
    grade === "B" ? "Bom" :
    grade === "C" ? "Regular" :
    grade === "D" ? "Ruim" :
    grade === "F" ? "Crítico" :
    "Sem dados";

  return (
    <Page
      title="Dashboard executivo"
      subtitle="O que vai faltar, o que está parado e o que comprar — em uma única visão."
    >
      {data.products_count === 0 && (
        <div data-testid="empty-state-banner" className="rounded-xl border border-primary/40 bg-primary/5 px-6 py-5 mb-6 flex flex-col md:flex-row items-start md:items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-primary/15 border border-primary/30 flex items-center justify-center shrink-0">
            <Package className="w-6 h-6 text-primary" />
          </div>
          <div className="flex-1">
            <div className="font-display font-semibold">Sua conta está vazia — vamos popular agora?</div>
            <p className="text-sm text-muted-foreground mt-1">
              Importe seu catálogo e histórico de vendas para ver previsões e alertas em segundos.
              Temos planilhas modelo prontas para download.
            </p>
          </div>
          <Link
            to="/app/integrations"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary hover:bg-primary/90 text-white font-medium text-sm transition"
            data-testid="empty-state-cta"
          >
            Importar dados <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      )}

      {data.is_demo_data && (
        <div data-testid="demo-data-banner" className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 mb-6 flex items-center gap-3 text-sm">
          <Package className="w-4 h-4 text-amber-400 shrink-0" />
          <span>
            <strong className="text-amber-300">Dados de demonstração</strong> — os números abaixo refletem o último período com vendas (até <strong>{data.revenue_anchor_date}</strong>). Importe seu CSV em{" "}
            <Link to="/app/integrations" className="text-primary underline">Integrações</Link>{" "}
            ou conecte sua API para ver dados em tempo real.
          </span>
        </div>
      )}

      {/* Top KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
        <div data-testid="kpi-health-score" className="p-6 rounded-xl border border-primary/30 bg-card glow-coral">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Saúde do estoque</div>
            <Heart className="w-4 h-4 text-primary" />
          </div>
          <div className="flex items-end gap-3 mt-3">
            <div className="font-display text-5xl font-bold text-primary">{data.health.score}</div>
            <div className={`font-display text-2xl font-bold ${gradeColor} mb-1`}>{grade}</div>
            <div className={`text-xs ${gradeColor} mb-2 font-medium`}>{gradeLabel}</div>
          </div>
          <div className="text-xs text-muted-foreground mt-2">{data.health.healthy_count} de {data.health.total} produtos saudáveis</div>
        </div>
        <StatCard testid="kpi-stockouts" icon={AlertTriangle} label="Em risco de ruptura" value={data.health.stockout_count} hint="produtos vão acabar em breve" />
        <StatCard testid="kpi-idle" icon={Package} label="Estoque parado" value={data.health.idle_count} hint="produtos sem giro recente" />
        <StatCard testid="kpi-revenue" icon={DollarSign} label="Receita projetada 30d" value={formatBRL(data.financial.expected_revenue)} hint={`Margem ${data.financial.margin_pct}%`} />
      </div>

      {/* Revenue trend */}
      <Section title="Tendência de receita (últimos 30 dias)">
        <div className="p-6 rounded-xl border border-border bg-card">
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={data.revenue_trend}>
              <CartesianGrid stroke="#1A2B47" strokeDasharray="3 3" />
              <XAxis dataKey="day" stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `R$${(v/1000).toFixed(0)}k`} />
              <Tooltip contentStyle={{ backgroundColor: "#0B1320", border: "1px solid #1A2B47", borderRadius: 12 }} formatter={(v) => formatBRL(v)} />
              <Line type="monotone" dataKey="revenue" stroke="#F05A3F" strokeWidth={2.5} dot={false} activeDot={{ r: 5, fill: "#F05A3F" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Section>

      {/* Top alerts + recommendations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section
          title="Vão faltar primeiro"
          action={<Link to="/app/alerts" className="text-xs text-primary hover:underline flex items-center gap-1">Ver tudo <ArrowRight className="w-3 h-3" /></Link>}
        >
          <div className="rounded-xl border border-border bg-card divide-y divide-border">
            {data.top_stockouts.length === 0 && <div className="p-6 text-sm text-muted-foreground">Nenhum produto em risco. </div>}
            {data.top_stockouts.map((p) => (
              <div key={p.product_id} data-testid={`stockout-${p.product_id}`} className="p-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium truncate">{p.name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{p.category} · estoque {p.stock} un · venda média {p.daily_avg_demand}/dia</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-display font-bold text-primary">{p.days_until_stockout.toFixed(0)}d</div>
                  <div className="text-xs text-muted-foreground">para ruptura</div>
                </div>
              </div>
            ))}
          </div>
        </Section>

        <Section
          title="Comprar agora (recomendado)"
          action={<Link to="/app/recommendations" className="text-xs text-primary hover:underline flex items-center gap-1">Ver tudo <ArrowRight className="w-3 h-3" /></Link>}
        >
          <div className="rounded-xl border border-border bg-card divide-y divide-border">
            {data.top_recommendations.length === 0 && <div className="p-6 text-sm text-muted-foreground">Nenhuma compra urgente.</div>}
            {data.top_recommendations.slice(0, 5).map((p) => (
              <div key={p.product_id} data-testid={`rec-${p.product_id}`} className="p-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium truncate">{p.name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{p.category} · lead time {p.lead_time_days}d</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-display font-bold text-primary">{p.suggested_purchase}</div>
                  <div className="text-xs text-muted-foreground">{formatBRL(p.suggested_purchase * p.cost)}</div>
                </div>
              </div>
            ))}
          </div>
        </Section>
      </div>

      {/* Critical alerts ribbon */}
      {data.critical_alerts.length > 0 && (
        <Section title="Alertas críticos">
          <div className="space-y-3">
            {data.critical_alerts.map((a) => (
              <div key={a.id} className="p-4 rounded-xl border border-red-500/30 bg-red-500/5 flex items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5" />
                  <div>
                    <div className="font-medium">{a.title}</div>
                    <div className="text-sm text-muted-foreground">{a.description}</div>
                  </div>
                </div>
                <SeverityBadge severity={a.severity} />
              </div>
            ))}
          </div>
        </Section>
      )}
    </Page>
  );
}
